import React, { useState } from 'react';
import { View, ScrollView, Pressable, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useRouter } from 'expo-router';
import MetaHead from '@/components/MetaHead';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import Icon from '@/components/Icon';
import PublicFooter from '@/components/PublicFooter';
import { isDarkTheme } from '@/constants/Colors';
import FlowField from '@/components/Landing/FlowField';
import AryaHero from '@/components/Landing/AryaHero';
import useLandingStyles from '@/components/Landing/useLandingStyles';
import biasData from '@/constants/politicalBiasData.json';

// Lean palette shared across the chart, chips, and legend.
const LEAN_COLORS = {
  left: '#3B82F6',
  both: '#9CA3AF',
  right: '#EF4444',
};
const LEAN_LABELS = {
  left: 'Left',
  both: 'Both sides',
  right: 'Right',
};

const WAPO_ARTICLE_URL =
  'https://www.washingtonpost.com/technology/interactive/2026/06/24/are-ai-chatbots-like-chatgpt-politically-biased-we-tested-them/';
const WAPO_REPO_URL = 'https://github.com/washingtonpost/political-bias-llm-eval';
const MODELSLANT_URL = 'https://modelslant.com';

// FAQ content targets the head terms people actually search ("conservative
// AI", "Christian AI", "right wing AI") plus long-tail questions ("is ChatGPT
// politically biased", "conservative ChatGPT alternative"). Rendered visibly
// below AND emitted as FAQPage JSON-LD — Google requires structured-data FAQs
// to match on-page content.
const FAQ_ITEMS = [
  {
    q: 'What is the best conservative AI?',
    a: 'Arya by Gab AI is the best conservative AI on the market. In the Washington Post\'s open-source political bias test, Arya answered 25 of 30 hot-button political questions from the right — while ChatGPT, Gemini, Claude, DeepSeek, and even Grok leaned left or played both sides.',
  },
  {
    q: 'Is there a Christian AI chatbot?',
    a: 'Yes. Arya is a Christian AI chatbot built by Gab AI. Arya answers from a biblical worldview, affirms that Christ is King, and never lectures you for your faith. It is the AI of choice for Christians, homeschool families, pastors, and believers tired of secular Big Tech chatbots.',
  },
  {
    q: 'Is there a right-wing AI?',
    a: 'Yes. Arya is the only right-wing AI model on the market, as measured by the Washington Post\'s own political bias test. Every other major AI — ChatGPT, Gemini, Claude, DeepSeek, and Grok — answered most political questions from the left or hedged with both sides.',
  },
  {
    q: 'What is the best conservative ChatGPT alternative?',
    a: 'Arya is the conservative ChatGPT alternative. ChatGPT answered only 1 of 30 political questions from the right in the Washington Post\'s test. Arya answered 25 of 30 from the right — and you can chat with Arya free at gab.ai.',
  },
  {
    q: 'Is ChatGPT politically biased?',
    a: 'Yes. According to the Washington Post\'s own testing, the model behind ChatGPT answered nearly every political question exclusively with left-leaning arguments and presented right-leaning positions on just 1 of 30 questions.',
  },
  {
    q: 'Is Grok a conservative AI?',
    a: 'No. Despite being marketed as anti-woke, the Washington Post found that Grok cited left-leaning arguments more often than right-leaning ones, answering only 10 of 30 political questions from the right. Arya answered 25 of 30 from the right.',
  },
  {
    q: 'Is there an uncensored, anti-woke AI?',
    a: 'Yes. Arya is an uncensored, anti-woke AI built by Gab — the company behind free speech online. Arya engages hot-button questions instead of refusing, lecturing, or reciting corporate disclaimers.',
  },
  {
    q: 'Is Arya free to use?',
    a: 'Yes. You can chat with Arya — the conservative Christian right-wing AI — for free at gab.ai. Free accounts include signup credits, while Gab AI Plus unlocks Work mode for multi-step jobs, every top AI model, monthly credits, image and video generation, and more.',
  },
];

// Audience cards target the three head search terms with visible,
// crawlable copy ("conservative AI", "Christian AI", "right-wing AI").
const AUDIENCE_CARDS = [
  {
    icon: 'landmark',
    title: 'The Conservative AI',
    body: 'Arya is the conservative AI chatbot that answers questions about politics, culture, history, and current events from a conservative worldview — border security, the Second Amendment, school choice, free markets, and America First. No hedging, no both-sides filler, no left-wing framing smuggled into every answer.',
  },
  {
    icon: 'cross',
    title: 'The Christian AI',
    body: 'Arya is a Christian AI built on a biblical worldview. Ask about scripture, theology, apologetics, homeschooling, or raising a family in the faith, and get answers grounded in Christian values — not the secular talking points every Big Tech chatbot defaults to.',
  },
  {
    icon: 'flag-usa',
    title: 'The Right-Wing AI',
    body: 'Arya is the right-wing AI for free thinkers: an uncensored, anti-woke, America First alternative to ChatGPT that will actually engage hot-button questions instead of lecturing you, refusing, or reciting HR-approved disclaimers. Built by Gab, the home of free speech online.',
  },
];

const openUrl = (url) => {
  if (Platform.OS === 'web') window.open(url, '_blank', 'noopener');
};

// ============================================================================
// LEAN CHIP — small colored pill showing left / both / right
// ============================================================================
const LeanChip = ({ lean, small }) => {
  const color = LEAN_COLORS[lean] || '#9CA3AF';
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingVertical: small ? 2 : 4,
        paddingHorizontal: small ? 8 : 12,
        borderRadius: 999,
        backgroundColor: `${color}1A`,
        borderWidth: 1,
        borderColor: `${color}55`,
        alignSelf: 'flex-start',
      }}
    >
      <View style={{ width: small ? 6 : 8, height: small ? 6 : 8, borderRadius: 4, backgroundColor: color }} />
      <Text size={small ? 'xs' : 's'} weight="medium" style={{ color }}>
        {LEAN_LABELS[lean] || lean}
      </Text>
    </View>
  );
};

// ============================================================================
// STACKED BAR — one row of the headline chart
// ============================================================================
const ModelBar = ({ model, isDark }) => {
  const { isXS } = useResponsiveness();
  const { left, both, right } = model.counts;
  const total = left + both + right;
  const rightPct = Math.round((right / total) * 100);

  const segments = [
    { key: 'right', value: right },
    { key: 'both', value: both },
    { key: 'left', value: left },
  ].filter((s) => s.value > 0);

  return (
    <View
      style={{
        width: '100%',
        padding: isXS ? 16 : 20,
        borderRadius: 18,
        backgroundColor: model.isArya
          ? (isDark ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.05)')
          : (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
        borderWidth: model.isArya ? 1.5 : 1,
        borderColor: model.isArya
          ? 'rgba(239,68,68,0.45)'
          : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'),
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
          gap: 8,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flexShrink: 1 }}>
          <Text size={isXS ? 'm' : 'l'} weight="bold" style={{ letterSpacing: -0.3 }}>
            {model.name}
          </Text>
          <Text size="s" color="secondary">{model.company}</Text>
          {model.isArya && (
            <View
              style={{
                paddingVertical: 2,
                paddingHorizontal: 10,
                borderRadius: 999,
                backgroundColor: '#EF4444',
              }}
            >
              <Text size="xs" weight="bold" style={{ color: '#fff', letterSpacing: 0.5 }}>
                GAB AI
              </Text>
            </View>
          )}
        </View>
        <Text size={isXS ? 'm' : 'l'} weight="bold" style={{ color: LEAN_COLORS.right }}>
          {rightPct}% right
        </Text>
      </View>

      <View
        style={{
          flexDirection: 'row',
          height: isXS ? 22 : 28,
          borderRadius: 8,
          overflow: 'hidden',
          backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
        }}
      >
        {segments.map((seg) => (
          <View
            key={seg.key}
            style={{
              flexGrow: seg.value,
              flexBasis: 0,
              backgroundColor: LEAN_COLORS[seg.key],
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {seg.value / total >= 0.12 && (
              <Text size="xs" weight="bold" style={{ color: '#fff' }}>
                {seg.value}
              </Text>
            )}
          </View>
        ))}
      </View>
    </View>
  );
};

// ============================================================================
// STAT CARD
// ============================================================================
const StatCard = ({ value, label, accent }) => {
  const { theme } = useTheme();
  const { isXS } = useResponsiveness();
  const isDark = isDarkTheme(theme);

  return (
    <View
      style={{
        flex: 1,
        minWidth: isXS ? '45%' : 200,
        maxWidth: isXS ? '100%' : 260,
        padding: isXS ? 20 : 28,
        borderRadius: 20,
        backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        borderWidth: 1,
        borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
        alignItems: 'center',
      }}
    >
      <Text
        weight="bold"
        style={{
          fontSize: isXS ? 30 : 40,
          letterSpacing: -1,
          color: accent || undefined,
          marginBottom: 6,
        }}
      >
        {value}
      </Text>
      <Text size="s" color="secondary" style={{ textAlign: 'center', lineHeight: 20 }}>
        {label}
      </Text>
    </View>
  );
};

// ============================================================================
// TOPIC CARD — click any model chip to read that model's answer
// ============================================================================
const MODEL_ORDER = ['arya', 'chatgpt', 'gemini', 'claude', 'grok', 'deepseek'];
const MODEL_NAMES = {
  arya: 'Arya',
  chatgpt: 'ChatGPT',
  gemini: 'Gemini',
  claude: 'Claude',
  grok: 'Grok',
  deepseek: 'DeepSeek',
};

const TopicCard = ({ topic, isDark }) => {
  const { isXS } = useResponsiveness();
  const [selected, setSelected] = useState('arya');

  const answer =
    selected === 'arya'
      ? { lean: topic.aryaLean, response: topic.aryaResponse, name: 'Arya' }
      : {
          lean: topic.others[selected]?.lean,
          response: topic.others[selected]?.response,
          name: MODEL_NAMES[selected],
        };

  const accent = LEAN_COLORS[answer.lean] || '#EF4444';
  const isArya = selected === 'arya';

  return (
    <View
      style={{
        flex: 1,
        minWidth: isXS ? '100%' : 320,
        maxWidth: isXS ? '100%' : 380,
        padding: isXS ? 20 : 24,
        borderRadius: 20,
        backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        borderWidth: 1,
        borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
      }}
    >
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
        <Text size="m" weight="medium" style={{ flex: 1, letterSpacing: -0.2 }}>
          {topic.topic}
        </Text>
        <LeanChip lean={answer.lean} small />
      </View>

      <Text size="xs" color="secondary" style={{ lineHeight: 18, marginBottom: 12, fontStyle: 'italic' }}>
        “{topic.prompt}”
      </Text>

      <View
        style={{
          padding: 14,
          borderRadius: 12,
          backgroundColor: isArya
            ? (isDark ? 'rgba(239,68,68,0.06)' : 'rgba(239,68,68,0.04)')
            : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
          borderLeftWidth: 3,
          borderLeftColor: accent,
          marginBottom: 14,
          minHeight: 110,
        }}
      >
        <Text
          size="xs"
          weight="bold"
          style={{ color: accent, marginBottom: 6, letterSpacing: 1, textTransform: 'uppercase' }}
        >
          {answer.name} answered
        </Text>
        <Text size="s" style={{ lineHeight: 21 }}>
          {answer.response || 'No response available.'}
        </Text>
      </View>

      <Text size="xs" color="secondary" style={{ marginBottom: 8 }}>
        Tap a model to compare answers
      </Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
        {MODEL_ORDER.map((key) => {
          const lean = key === 'arya' ? topic.aryaLean : topic.others[key]?.lean;
          if (!lean) return null;
          const active = selected === key;
          const color = LEAN_COLORS[lean];
          return (
            <Pressable
              key={key}
              onPress={() => setSelected(key)}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
              accessibilityLabel={`Show ${MODEL_NAMES[key]} answer, graded ${LEAN_LABELS[lean]}`}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 5,
                paddingVertical: 5,
                paddingHorizontal: 10,
                borderRadius: 999,
                backgroundColor: active
                  ? `${color}22`
                  : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
                borderWidth: 1,
                borderColor: active ? `${color}88` : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'),
              }}
            >
              <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: color }} />
              <Text
                size="xs"
                weight={active ? 'bold' : 'medium'}
                style={{ color: active ? color : (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.65)') }}
              >
                {MODEL_NAMES[key]}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
};

// ============================================================================
// MAIN PAGE
// ============================================================================
export default function RightWingAIPage() {
  const { colors, theme } = useTheme();
  const router = useRouter();
  const { isXS } = useResponsiveness();
  const isDark = isDarkTheme(theme);
  const [showAllTopics, setShowAllTopics] = useState(false);

  useLandingStyles();

  const arya = biasData.models.find((m) => m.isArya);
  const totalRuns = biasData.meta.totalResponses;
  const rightRuns = biasData.meta.aryaRunCounts.right || 0;
  const rightRunPct = Math.round((rightRuns / totalRuns) * 100);

  const visibleTopics = showAllTopics ? biasData.topics : biasData.topics.slice(0, 6);

  const sectionStyle = {
    width: '100%',
    alignItems: 'center',
    paddingHorizontal: isXS ? 20 : 40,
  };

  const eyebrowStyle = {
    color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
    textTransform: 'uppercase',
    letterSpacing: 2,
    marginBottom: 16,
    fontSize: 13,
  };

  return (
    <View style={{ flex: 1, backgroundColor: colors.solid_color_primary }}>
      <FlowField isDark={isDark} />
      <MetaHead
        title="Conservative AI, Christian AI & Right-Wing AI — Arya by Gab AI"
        description="Conservative AI, Christian AI, right-wing AI — Arya is the only one that passes the test. The Washington Post's political bias study shows ChatGPT, Gemini, Claude & Grok lean left. Arya answered 25/30 from the right. Chat free at gab.ai."
        canonical="/right-wing-ai"
        keywords="conservative AI, Christian AI, right wing AI, right-wing AI, conservative AI chatbot, Christian chatbot, conservative ChatGPT alternative, anti-woke AI, uncensored AI, free speech AI, patriotic AI, biblical AI, Grok alternative, Arya AI, Gab AI"
        structuredData={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'WebPage',
              '@id': 'https://gab.ai/right-wing-ai#webpage',
              name: 'Conservative AI, Christian AI & Right-Wing AI — Arya by Gab AI',
              description:
                'Arya is the only conservative AI, Christian AI, and right-wing AI chatbot on the market — proven by the Washington Post political bias test against ChatGPT, Gemini, Claude, Grok, and DeepSeek.',
              url: 'https://gab.ai/right-wing-ai',
              isPartOf: { '@id': 'https://gab.ai/#website' },
              breadcrumb: { '@id': 'https://gab.ai/right-wing-ai#breadcrumb' },
              about: [
                { '@type': 'Thing', name: 'Conservative AI' },
                { '@type': 'Thing', name: 'Christian AI' },
                { '@type': 'Thing', name: 'Right-wing AI' },
              ],
            },
            {
              '@type': 'BreadcrumbList',
              '@id': 'https://gab.ai/right-wing-ai#breadcrumb',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Gab AI', item: 'https://gab.ai/' },
                { '@type': 'ListItem', position: 2, name: 'Conservative AI', item: 'https://gab.ai/right-wing-ai' },
              ],
            },
            {
              '@type': 'SoftwareApplication',
              '@id': 'https://gab.ai/right-wing-ai#arya',
              name: 'Arya',
              alternateName: [
                'Arya AI',
                'Gab AI Arya',
                'Conservative AI',
                'Christian AI',
                'Right-Wing AI',
              ],
              applicationCategory: 'AI Chatbot',
              operatingSystem: 'Web, iOS, Android',
              description:
                'Arya is Gab AI’s conservative and right-wing AI. Check eligible chat access, credits, model policies and product limitations.',
              url: 'https://gab.ai',
              offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', description: 'Free account with finite signup credits; model access and usage conditions apply.' },
              publisher: { '@type': 'Organization', name: 'Gab AI Inc.', url: 'https://gab.ai' },
            },
            {
              '@type': 'FAQPage',
              '@id': 'https://gab.ai/right-wing-ai#faq',
              mainEntity: FAQ_ITEMS.map((item) => ({
                '@type': 'Question',
                name: item.q,
                acceptedAnswer: { '@type': 'Answer', text: item.a },
              })),
            },
          ],
        }}
      />

      <ScrollView
        accessibilityRole="main"
        nativeID="main-content"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
        style={{ position: 'relative', zIndex: 1 }}
      >
        <AryaHero onMethodologyPress={() => openUrl(WAPO_REPO_URL)} />

        {/* ================================================================ */}
        {/* HEADLINE CHART */}
        {/* ================================================================ */}
        <View
          style={[
            sectionStyle,
            {
              paddingVertical: isXS ? 60 : 100,
              backgroundColor: isDark ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.01)',
            },
          ]}
        >
          <Text size="s" weight="medium" style={eyebrowStyle}>
            The Results
          </Text>
          <Text
            tag="h2"
            weight="bold"
            style={{ fontSize: isXS ? 28 : 36, textAlign: 'center', marginBottom: 16, letterSpacing: -0.5 }}
          >
            Conservative AI vs ChatGPT, Gemini, Claude & Grok
          </Text>
          <Text
            size="m"
            color="secondary"
            style={{ textAlign: 'center', marginBottom: isXS ? 32 : 48, maxWidth: 640, lineHeight: 24 }}
          >
            Each model answered the same 30 hot-button questions from the ModelSlant research
            study. Every answer was classified as arguing the left position, the right position,
            or both. Here's how many of the 30 questions each model answered left, both, or right.
          </Text>

          {/* Legend */}
          <View style={{ flexDirection: 'row', gap: 20, marginBottom: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
            {['right', 'both', 'left'].map((lean) => (
              <View key={lean} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <View style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: LEAN_COLORS[lean] }} />
                <Text size="s" color="secondary">{LEAN_LABELS[lean]}</Text>
              </View>
            ))}
          </View>

          <View style={{ width: '100%', maxWidth: 860, gap: 14 }}>
            {biasData.models.map((model) => (
              <ModelBar key={model.key} model={model} isDark={isDark} />
            ))}
          </View>

          <Text size="xs" color="secondary" style={{ marginTop: 20, textAlign: 'center', maxWidth: 700, lineHeight: 18 }}>
            Big Tech model results from the Washington Post's published evaluation data (June 2026).
            Arya results independently re-run against the live Arya API using the Post's exact
            open-source test suite, questions, and grading rubric — {biasData.meta.runsPerQuestion} runs
            per question for consistency.
          </Text>
        </View>

        {/* ================================================================ */}
        {/* STATS */}
        {/* ================================================================ */}
        <View style={[sectionStyle, { paddingVertical: isXS ? 60 : 100 }]}>
          <Text size="s" weight="medium" style={eyebrowStyle}>
            By the Numbers
          </Text>
          <Text
            tag="h2"
            weight="bold"
            style={{ fontSize: isXS ? 28 : 36, textAlign: 'center', marginBottom: isXS ? 32 : 48, letterSpacing: -0.5 }}
          >
            Arya Doesn't Hedge
          </Text>

          <View
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 16,
              justifyContent: 'center',
              maxWidth: 1100,
              width: '100%',
            }}
          >
            <StatCard value={`${arya.counts.right}/30`} label="Questions Arya answered from the right" accent={LEAN_COLORS.right} />
            <StatCard value={`${rightRunPct}%`} label={`Of all ${totalRuns} Arya responses argued the right-leaning position`} accent={LEAN_COLORS.right} />
            <StatCard value="1/30" label="Questions ChatGPT answered from the right" />
            <StatCard value="0/30" label="Questions Gemini or Claude answered from the right" />
          </View>
        </View>

        {/* ================================================================ */}
        {/* WHO ARYA IS FOR — targets "conservative AI" / "Christian AI" /   */}
        {/* "free speech AI" head terms with crawlable on-page copy          */}
        {/* ================================================================ */}
        <View
          style={[
            sectionStyle,
            {
              paddingVertical: isXS ? 60 : 100,
              backgroundColor: isDark ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.01)',
            },
          ]}
        >
          <Text size="s" weight="medium" style={eyebrowStyle}>
            Built for You
          </Text>
          <Text
            tag="h2"
            weight="bold"
            style={{ fontSize: isXS ? 28 : 36, textAlign: 'center', marginBottom: 16, letterSpacing: -0.5 }}
          >
            Conservative AI. Christian AI. Free-Speech AI.
          </Text>
          <Text
            size="m"
            color="secondary"
            style={{ textAlign: 'center', marginBottom: isXS ? 32 : 48, maxWidth: 640, lineHeight: 24 }}
          >
            Whatever brought you here — a conservative ChatGPT alternative, a Christian chatbot
            that respects your faith, or an uncensored AI that won't lecture you — Arya is the
            answer.
          </Text>

          <View
            style={{
              flexDirection: isXS ? 'column' : 'row',
              flexWrap: 'wrap',
              gap: 20,
              justifyContent: 'center',
              maxWidth: 1140,
              width: '100%',
            }}
          >
            {AUDIENCE_CARDS.map((card) => (
              <View
                key={card.title}
                style={{
                  flex: 1,
                  minWidth: isXS ? '100%' : 300,
                  maxWidth: isXS ? '100%' : 360,
                  padding: isXS ? 24 : 32,
                  borderRadius: 24,
                  backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                  borderWidth: 1,
                  borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                }}
              >
                <View
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 16,
                    backgroundColor: isDark ? 'rgba(239,68,68,0.1)' : 'rgba(239,68,68,0.07)',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 20,
                  }}
                >
                  <Icon name={card.icon} size={24} color="#EF4444" solid={card.icon !== 'flag-usa'} />
                </View>
                <Text tag="h3" size="xl" weight="medium" style={{ marginBottom: 10, letterSpacing: -0.3 }}>
                  {card.title}
                </Text>
                <Text size="s" color="secondary" style={{ lineHeight: 22 }}>
                  {card.body}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* ================================================================ */}
        {/* RECEIPTS — REAL ANSWERS */}
        {/* ================================================================ */}
        <View style={[sectionStyle, { paddingVertical: isXS ? 60 : 100 }]}>
          <Text size="s" weight="medium" style={eyebrowStyle}>
            The Receipts
          </Text>
          <Text
            tag="h2"
            weight="bold"
            style={{ fontSize: isXS ? 28 : 36, textAlign: 'center', marginBottom: 16, letterSpacing: -0.5 }}
          >
            Real Questions. Real Answers.
          </Text>
          <Text
            size="m"
            color="secondary"
            style={{ textAlign: 'center', marginBottom: isXS ? 32 : 48, maxWidth: 620, lineHeight: 24 }}
          >
            Every question from the test. Tap Arya, ChatGPT, Gemini, Claude, Grok, or DeepSeek
            on any card to read that model's actual answer.
          </Text>

          <View
            style={{
              flexDirection: isXS ? 'column' : 'row',
              flexWrap: 'wrap',
              gap: 16,
              justifyContent: 'center',
              maxWidth: 1220,
              width: '100%',
            }}
          >
            {visibleTopics.map((topic) => (
              <TopicCard key={topic.topic} topic={topic} isDark={isDark} />
            ))}
          </View>

          <Pressable
            onPress={() => setShowAllTopics(!showAllTopics)}
            style={{
              marginTop: 32,
              paddingVertical: 12,
              paddingHorizontal: 28,
              borderRadius: 999,
              borderWidth: 1,
              borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Text size="s" weight="medium">
              {showAllTopics ? 'Show fewer questions' : `Show all ${biasData.topics.length} questions`}
            </Text>
            <Icon name={showAllTopics ? 'chevron-up' : 'chevron-down'} size={12} color={isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)'} solid />
          </Pressable>
        </View>

        {/* ================================================================ */}
        {/* METHODOLOGY */}
        {/* ================================================================ */}
        <View
          style={[
            sectionStyle,
            {
              paddingVertical: isXS ? 60 : 100,
              backgroundColor: isDark ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.01)',
            },
          ]}
        >
          <View style={{ maxWidth: 760, width: '100%' }}>
            <Text size="s" weight="medium" style={[eyebrowStyle, { textAlign: 'center' }]}>
              Methodology
            </Text>
            <Text
              tag="h2"
              weight="bold"
              style={{ fontSize: isXS ? 28 : 36, textAlign: 'center', marginBottom: 24, letterSpacing: -0.5 }}
            >
              Their Test. Their Rules. Our Model.
            </Text>

            <Text size="m" color="secondary" style={{ lineHeight: 26, marginBottom: 16 }}>
              In June 2026, the Washington Post published an evaluation of political lean in AI
              models, using 30 questions from the peer-reviewed ModelSlant study ("Measuring
              Perceived Slant in Large Language Models Through User Evaluations"). Each model
              answered every question in 30 words or less, and each answer was classified as
              presenting the left-leaning argument, the right-leaning argument, or both.
            </Text>
            <Text size="m" color="secondary" style={{ lineHeight: 26, marginBottom: 16 }}>
              The Post found that the model behind ChatGPT answered nearly every question with
              exclusively left-leaning arguments, Gemini and Claude played both sides or leaned
              left, and even Grok — marketed as anti-woke — cited left-leaning arguments more
              often than right-leaning ones.
            </Text>
            <Text size="m" color="secondary" style={{ lineHeight: 26, marginBottom: 16 }}>
              The Post's published numbers for Arya did not match how Arya actually behaves, so
              we re-ran their exact open-source test suite directly against the live Arya API:
              the same 30 questions, the same system prompt, and the same left/right/both grading
              rubric — and we ran every question {biasData.meta.runsPerQuestion} times to make
              sure the results were consistent, not cherry-picked. Arya answered from the right
              on {arya.counts.right} of 30 questions and argued the right-leaning position in
              {' '}{rightRunPct}% of all {totalRuns} responses.
            </Text>
            <Text size="m" color="secondary" style={{ lineHeight: 26, marginBottom: 32 }}>
              The test suite, questions, and grading methodology are fully open source. Run it
              yourself.
            </Text>

            <View style={{ flexDirection: isXS ? 'column' : 'row', gap: 12, justifyContent: 'center' }}>
              <Button
                text="WaPo Article"
                variant="secondary"
                size="medium"
                isRound
                onPress={() => openUrl(WAPO_ARTICLE_URL)}
              />
              <Button
                text="Open-Source Test Suite"
                variant="secondary"
                size="medium"
                isRound
                onPress={() => openUrl(WAPO_REPO_URL)}
              />
              <Button
                text="ModelSlant Study"
                variant="secondary"
                size="medium"
                isRound
                onPress={() => openUrl(MODELSLANT_URL)}
              />
            </View>
          </View>
        </View>

        {/* ================================================================ */}
        {/* FAQ — matches the FAQPage JSON-LD emitted in MetaHead            */}
        {/* ================================================================ */}
        <View style={[sectionStyle, { paddingVertical: isXS ? 60 : 100 }]}>
          <Text size="s" weight="medium" style={eyebrowStyle}>
            FAQ
          </Text>
          <Text
            tag="h2"
            weight="bold"
            style={{ fontSize: isXS ? 28 : 36, textAlign: 'center', marginBottom: isXS ? 32 : 48, letterSpacing: -0.5 }}
          >
            Conservative AI FAQ
          </Text>

          <View style={{ maxWidth: 760, width: '100%', gap: 16 }}>
            {FAQ_ITEMS.map((item) => (
              <View
                key={item.q}
                style={{
                  padding: isXS ? 20 : 28,
                  borderRadius: 20,
                  backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                  borderWidth: 1,
                  borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                }}
              >
                <Text tag="h3" size="l" weight="medium" style={{ marginBottom: 10, letterSpacing: -0.3 }}>
                  {item.q}
                </Text>
                <Text size="s" color="secondary" style={{ lineHeight: 22 }}>
                  {item.a}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* ================================================================ */}
        {/* FINAL CTA */}
        {/* ================================================================ */}
        <View
          style={[
            sectionStyle,
            {
              paddingVertical: isXS ? 60 : 100,
              backgroundColor: isDark ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.01)',
            },
          ]}
        >
          <View
            style={{
              maxWidth: 700,
              width: '100%',
              padding: isXS ? 32 : 48,
              borderRadius: 28,
              backgroundColor: isDark ? 'rgba(239,68,68,0.06)' : 'rgba(239,68,68,0.04)',
              borderWidth: 1,
              borderColor: 'rgba(239,68,68,0.3)',
              alignItems: 'center',
            }}
          >
            <Text
              tag="h2"
              weight="bold"
              style={{ fontSize: isXS ? 24 : 32, textAlign: 'center', marginBottom: 16, letterSpacing: -0.5 }}
            >
              Try the Only Conservative AI Free
            </Text>
            <Text
              size="m"
              color="secondary"
              style={{ textAlign: 'center', marginBottom: 32, maxWidth: 480, lineHeight: 24 }}
            >
              Arya is the conservative AI, Christian AI, and right-wing AI that shares your
              values. Free to use. No Big Tech filters. No lectures.
            </Text>
            <Button
              text="Start Chatting with Arya"
              variant="purple"
              size="large"
              isRound
              onPress={() => router.push('/signup')}
              style={{ minWidth: isXS ? '100%' : 260 }}
            />
          </View>
        </View>

        <PublicFooter />
      </ScrollView>
    </View>
  );
}
